import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Interruptor, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';

export const TIPOS_DE_CAMPO = [
  'texto',
  'texto_largo',
  'texto_enriquecido',
  'numero',
  'fecha',
  'si_no',
  'lista',
  'imagen',
  'archivo',
] as const;

export type TipoDeCampo = (typeof TIPOS_DE_CAMPO)[number];
export type EntidadConCampos = 'articulo' | 'tercero';

export interface CampoPersonalizado {
  readonly id: string;
  readonly entidad: EntidadConCampos;
  readonly codigo: string;
  readonly nombre: string;
  readonly tipo: TipoDeCampo;
  readonly opciones: string[];
  readonly obligatorio: boolean;
  readonly ayuda: string | null;
  readonly orden: number;
  readonly activo: boolean;
}

const ENTIDADES: readonly EntidadConCampos[] = ['articulo', 'tercero'];

/**
 * Los campos que cada comercio agrega a sus maestros.
 *
 * ## Por qué existe esta pantalla
 *
 * Todo comercio tiene tres o cuatro datos propios —el despacho de importación, la medida de la
 * caja, quién trajo al cliente— que ningún sistema previó. Sin un lugar donde ponerlos terminan
 * adentro de la descripción, y ahí **no se pueden buscar, ni filtrar, ni listar**.
 *
 * ## Lo que la pantalla no deja hacer
 *
 * **Cambiarle el tipo a un campo.** Convertiría «sí» en un número y «2027-12-01» en texto suelto.
 * Se desactiva y se crea otro, que es lo que de verdad se quiere.
 */
export function CamposPersonalizados(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();
  const puedeAdministrar = usePermiso('EMPRESA.CAMPO.ADMINISTRAR');

  const [entidad, setEntidad] = useState<EntidadConCampos>('articulo');
  const [creando, setCreando] = useState(false);
  const [formulario, setFormulario] = useState({
    codigo: '',
    nombre: '',
    tipo: 'texto' as TipoDeCampo,
    opciones: '',
    obligatorio: false,
    ayuda: '',
  });
  const [corrigiendo, setCorrigiendo] = useState<CampoPersonalizado | null>(null);
  const [edicion, setEdicion] = useState({
    nombre: '',
    opciones: '',
    obligatorio: false,
    ayuda: '',
  });
  const [error, setError] = useState<string | null>(null);

  const campos = useQuery({
    queryKey: ['campos', entidad],
    queryFn: () => pedir<CampoPersonalizado[]>(`/campos?entidad=${entidad}`),
  });

  const refrescar = async (): Promise<void> => {
    setCreando(false);
    setCorrigiendo(null);
    setError(null);
    await cola.invalidateQueries({ queryKey: ['campos'] });
  };
  const alFallar = (fallo: unknown): void => {
    setError(textoDeError(fallo));
  };

  /** Las opciones se escriben una por línea: separarlas por coma rompe la que lleva una coma. */
  const comoLista = (texto: string): string[] =>
    texto
      .split('\n')
      .map((linea) => linea.trim())
      .filter((linea) => linea !== '');

  const crear = useMutation({
    mutationFn: () =>
      pedir<{ id: string }>('/campos', {
        method: 'POST',
        cuerpo: {
          entidad,
          codigo: formulario.codigo.trim(),
          nombre: formulario.nombre.trim(),
          tipo: formulario.tipo,
          ...(formulario.tipo === 'lista' ? { opciones: comoLista(formulario.opciones) } : {}),
          obligatorio: formulario.obligatorio,
          ...(formulario.ayuda.trim() === '' ? {} : { ayuda: formulario.ayuda.trim() }),
        },
      }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const corregir = useMutation({
    mutationFn: () =>
      pedir<void>(`/campos/${corrigiendo?.id ?? ''}`, {
        method: 'PATCH',
        cuerpo: {
          nombre: edicion.nombre.trim(),
          ...(corrigiendo?.tipo === 'lista' ? { opciones: comoLista(edicion.opciones) } : {}),
          obligatorio: edicion.obligatorio,
          ayuda: edicion.ayuda.trim(),
        },
      }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const alternar = useMutation({
    mutationFn: (fila: CampoPersonalizado) =>
      pedir<void>(`/campos/${fila.id}`, { method: 'PATCH', cuerpo: { activo: !fila.activo } }),
    onSuccess: refrescar,
    onError: alFallar,
  });

  const columnas: Columna<CampoPersonalizado>[] = [
    {
      clave: 'nombre',
      titulo: t('campos.nombre'),
      contenido: (fila) => (
        <>
          {fila.nombre}
          <span className="block text-apoyo text-texto-tenue">
            <span className="numero">{fila.codigo}</span>
          </span>
        </>
      ),
    },
    {
      clave: 'tipo',
      titulo: t('campos.tipo'),
      contenido: (fila) => (
        <>
          {t(`campos.tipos.${fila.tipo}`)}
          {fila.tipo === 'lista' ? (
            <span className="block text-apoyo text-texto-tenue">{fila.opciones.join(' · ')}</span>
          ) : null}
        </>
      ),
    },
    {
      clave: 'obligatorio',
      titulo: t('campos.obligatorio'),
      contenido: (fila) =>
        fila.obligatorio ? <Insignia tono="atencion">{t('campos.si')}</Insignia> : null,
    },
    {
      clave: 'estado',
      titulo: t('campos.estado'),
      contenido: (fila) => (
        <Insignia tono={fila.activo ? 'bien' : 'neutro'}>
          {fila.activo ? t('campos.activo') : t('campos.inactivo')}
        </Insignia>
      ),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) =>
        !puedeAdministrar ? null : (
          <div className="flex justify-end gap-1">
            <Boton
              onClick={() => {
                setCorrigiendo(fila);
                setEdicion({
                  nombre: fila.nombre,
                  opciones: fila.opciones.join('\n'),
                  obligatorio: fila.obligatorio,
                  ayuda: fila.ayuda ?? '',
                });
                setError(null);
              }}
            >
              {t('acciones.corregir')}
            </Boton>
            {/* No hay borrar: borrar el campo se lleva los valores cargados en cada ficha, y eso
                no se puede deshacer. Se desactiva y deja de pedirse. */}
            <Boton
              cargando={alternar.isPending}
              onClick={() => {
                alternar.mutate(fila);
              }}
            >
              {fila.activo ? t('campos.desactivar') : t('campos.activar')}
            </Boton>
          </div>
        ),
    },
  ];

  return (
    <>
      <Encabezado
        titulo={t('campos.titulo')}
        descripcion={t('campos.descripcion')}
        acciones={
          puedeAdministrar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setFormulario({
                  codigo: '',
                  nombre: '',
                  tipo: 'texto',
                  opciones: '',
                  obligatorio: false,
                  ayuda: '',
                });
                setCreando(true);
                setError(null);
              }}
            >
              {t('campos.nuevo')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        <div className="mb-3 max-w-xs">
          <Selector
            etiqueta={t('campos.deQueFicha')}
            value={entidad}
            onChange={(evento) => {
              setEntidad(evento.target.value === 'tercero' ? 'tercero' : 'articulo');
            }}
          >
            {ENTIDADES.map((posible) => (
              <option key={posible} value={posible}>
                {t(`campos.entidades.${posible}`)}
              </option>
            ))}
          </Selector>
        </div>

        {campos.isPending ? <Cargando /> : null}
        {campos.isError ? <Problema error={campos.error} /> : null}
        {campos.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={campos.data}
            claveDeFila={(fila) => fila.id}
            vacio={<Vacio titulo={t('campos.vacio')} descripcion={t('campos.paraQueSirven')} />}
          />
        ) : null}
        {error === null ? null : (
          <p role="alert" className="mt-3 text-apoyo text-mal">
            {error}
          </p>
        )}
      </Tarjeta>

      <Dialogo
        abierto={creando}
        titulo={t('campos.nuevo')}
        onCerrar={() => {
          setCreando(false);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            disabled={formulario.codigo.trim() === '' || formulario.nombre.trim() === ''}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('campos.nombre')}
            obligatorio
            value={formulario.nombre}
            maxLength={80}
            onChange={(evento) => {
              const nombre = evento.target.value;
              setFormulario((previo) => ({
                ...previo,
                nombre,
                // El código se propone desde el nombre y se puede corregir: nadie quiere escribir
                // «despacho_de_importacion» a mano, y dejarlo vacío obliga a inventarlo igual.
                codigo:
                  previo.codigo === '' || previo.codigo === aCodigo(previo.nombre)
                    ? aCodigo(nombre)
                    : previo.codigo,
              }));
            }}
          />
          <Campo
            etiqueta={t('campos.codigo')}
            obligatorio
            value={formulario.codigo}
            maxLength={40}
            ayuda={t('campos.codigoAyuda')}
            onChange={(evento) => {
              setFormulario({ ...formulario, codigo: aCodigo(evento.target.value) });
            }}
          />
          <Selector
            etiqueta={t('campos.tipo')}
            value={formulario.tipo}
            ayuda={t('campos.tipoAyuda')}
            onChange={(evento) => {
              setFormulario({ ...formulario, tipo: evento.target.value as TipoDeCampo });
            }}
          >
            {TIPOS_DE_CAMPO.map((tipo) => (
              <option key={tipo} value={tipo}>
                {t(`campos.tipos.${tipo}`)}
              </option>
            ))}
          </Selector>
          {formulario.tipo === 'lista' ? (
            <label className="flex flex-col gap-1">
              <span className="text-apoyo font-medium">{t('campos.opciones')}</span>
              <textarea
                rows={4}
                value={formulario.opciones}
                onChange={(evento) => {
                  setFormulario({ ...formulario, opciones: evento.target.value });
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1.5 text-cuerpo"
              />
              <span className="text-apoyo text-texto-tenue">{t('campos.opcionesAyuda')}</span>
            </label>
          ) : null}
          <Campo
            etiqueta={t('campos.ayuda')}
            value={formulario.ayuda}
            maxLength={200}
            onChange={(evento) => {
              setFormulario({ ...formulario, ayuda: evento.target.value });
            }}
          />
          <Interruptor
            etiqueta={t('campos.obligatorio')}
            ayuda={t('campos.obligatorioAyuda')}
            checked={formulario.obligatorio}
            onChange={(evento) => {
              setFormulario({ ...formulario, obligatorio: evento.target.checked });
            }}
          />
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      <Dialogo
        abierto={corrigiendo !== null}
        titulo={t('campos.corregir')}
        descripcion={t('campos.tipoNoSeCambia')}
        onCerrar={() => {
          setCorrigiendo(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={corregir.isPending}
            onClick={() => {
              corregir.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          <Campo
            etiqueta={t('campos.nombre')}
            value={edicion.nombre}
            maxLength={80}
            onChange={(evento) => {
              setEdicion({ ...edicion, nombre: evento.target.value });
            }}
          />
          {corrigiendo?.tipo === 'lista' ? (
            <label className="flex flex-col gap-1">
              <span className="text-apoyo font-medium">{t('campos.opciones')}</span>
              <textarea
                rows={4}
                value={edicion.opciones}
                onChange={(evento) => {
                  setEdicion({ ...edicion, opciones: evento.target.value });
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1.5 text-cuerpo"
              />
              <span className="text-apoyo text-texto-tenue">{t('campos.opcionEnUso')}</span>
            </label>
          ) : null}
          <Campo
            etiqueta={t('campos.ayuda')}
            value={edicion.ayuda}
            maxLength={200}
            onChange={(evento) => {
              setEdicion({ ...edicion, ayuda: evento.target.value });
            }}
          />
          <Interruptor
            etiqueta={t('campos.obligatorio')}
            checked={edicion.obligatorio}
            onChange={(evento) => {
              setEdicion({ ...edicion, obligatorio: evento.target.checked });
            }}
          />
          {error === null ? null : (
            <p role="alert" className="text-apoyo text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}

/**
 * De un nombre a un código: «Despacho de importación» → `despacho_de_importacion`.
 *
 * Sin acentos y sin eñes, porque es una clave: la va a escribir alguien en una consulta el día que
 * haya informes, y `nº_de_serie` es una clave que nadie acierta a la primera.
 */
export function aCodigo(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}
